import React, { useState, useId } from 'react';
import { ChevronDownIcon, QuoteIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
type TestimonialVariant = 'about' | 'home';
interface ExpandableTestimonialProps {
  author: string;
  role: string;
  quote: string;
  image?: string;
  initials: string;
  variant: TestimonialVariant;
  excerptLength?: number;
}
export function ExpandableTestimonial({
  author,
  role,
  quote,
  image,
  initials,
  variant,
  excerptLength = 220
}: ExpandableTestimonialProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const quoteId = useId();
  const isHomeVariant = variant === 'home';
  const preview = createExcerpt(quote, excerptLength);
  return <motion.article layout className={isHomeVariant ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-3xl p-8 lg:p-12' : 'bg-black/30 border border-white/10 rounded-xl p-6 hover:bg-black/40 transition-colors duration-300'} whileHover={{
    scale: isHomeVariant ? 1.02 : 1.01,
    y: isHomeVariant ? 0 : -3
  }} transition={{
    layout: {
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    }
  }} data-id="element-170">
      {isHomeVariant && <QuoteIcon className="w-10 h-10 text-purple-400 mb-6" data-id="element-171" />}

      <div id={quoteId} aria-live="polite" data-id="element-172">
        <AnimatePresence initial={false} mode="wait" data-id="element-173">
          <motion.blockquote key={isExpanded ? 'expanded' : 'collapsed'} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.2
        }} className={isHomeVariant ? 'text-lg md:text-xl text-white font-medium leading-relaxed' : 'text-white/80 italic leading-relaxed'} data-id="element-174">
            “{isExpanded ? quote : preview}”
          </motion.blockquote>
        </AnimatePresence>
      </div>

      <button type="button" className="group mt-4 inline-flex items-center gap-2 rounded-lg text-sm font-semibold text-white/75 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" aria-expanded={isExpanded} aria-controls={quoteId} onClick={() => setIsExpanded(expanded => !expanded)} data-id="element-175">
        {isExpanded ? 'Show less' : 'Read full testimonial'}
        <ChevronDownIcon aria-hidden="true" className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-176" />
      </button>

      <div className={isHomeVariant ? 'flex items-center gap-4 mt-8' : 'flex items-center gap-4 mt-6'} data-id="element-177">
        {image ? <img src={image} alt={author} className="w-14 h-14 rounded-full object-cover" data-id="element-178" /> : <div aria-hidden="true" className="w-14 h-14 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-sm font-semibold text-white" data-id="element-179">
            {initials}
          </div>}
        <div data-id="element-180">
          <p className="text-white font-semibold" data-id="element-181">{author}</p>
          <p className={isHomeVariant ? 'text-white/60' : 'text-white/60 text-sm'} data-id="element-182">
            {role}
          </p>
        </div>
      </div>
    </motion.article>;
}
function createExcerpt(text: string, maxLength: number) {
  if (text.length <= maxLength) {
    return text;
  }
  const sentenceEnd = text.lastIndexOf('.', maxLength);
  if (sentenceEnd > maxLength * 0.55) {
    return text.slice(0, sentenceEnd + 1);
  }
  const wordEnd = text.lastIndexOf(' ', maxLength);
  return `${text.slice(0, wordEnd > 0 ? wordEnd : maxLength)}…`;
}